import { FC, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { GettingStartedCard } from "./getting-started-card";
import { GettingStartedCardStep } from "./getting-started-card.types";
import styled from "@emotion/styled";
import { GettingStartedHelpModal } from "./getting-started-help-modal";
import { STEP_IDS, type StepId } from "@/store/getting-started";
import { windowModule } from "@/modules/window";
import { APP_CONFIG } from "@/domains/global/config";
import { StyledInlineLink } from "./getting-started-card.style";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";

/**
 * Container component that connects GettingStartedCard to AppStoreGettingStartedStore.
 * Handles all data transformation and callback logic.
 */
export const GettingStartedCardContainer: FC = observer(function GettingStartedCardContainer() {
  const { store } = useAppStore();
  const gettingStartedStore = store.gettingStarted;
  const [helpModalStepId, setHelpModalStepId] = useState<StepId | null>(null);

  // Mark that user has seen the getting started card when component mounts
  useEffect(() => {
    gettingStartedStore.markGettingStartedCardSeen();
  }, [gettingStartedStore]);

  const handleHelpClick = useCallback((stepId: string) => {
    setHelpModalStepId(stepId as StepId);
  }, []);

  const handleCloseHelpModal = useCallback(() => {
    setHelpModalStepId(null);
  }, []);

  const handleExploreMoreClick = useCallback(() => {
    void windowModule.openInNewTab({ url: new URL("https://help.mem.ai/") });
  }, []);

  const handleIosAppClick = useCallback(() => {
    gettingStartedStore.markStepComplete(STEP_IDS.GET_APP_OR_EXTENSION);
    void windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.IOS_APP_V2) });
  }, [gettingStartedStore]);

  const handleChromeExtensionClick = useCallback(() => {
    gettingStartedStore.markStepComplete(STEP_IDS.GET_APP_OR_EXTENSION);
    store.settingsModal.open(SettingsTabId.ChromeExtensionPage);
  }, [gettingStartedStore, store.settingsModal]);

  // Transform store steps to component steps format
  // No useMemo needed - observer will track MobX observables and re-render automatically
  const steps: GettingStartedCardStep[] = gettingStartedStore.steps.map(step => {
    const config = gettingStartedStore.getStepConfig(step.id);

    // Build title - step 4 has inline links, others use plain string
    let title: GettingStartedCardStep["title"] = step.title;
    if (step.id === STEP_IDS.GET_APP_OR_EXTENSION) {
      title = (
        <>
          {"Get the "}
          <StyledInlineLink onClick={handleIosAppClick}>iOS app</StyledInlineLink>
          {" or "}
          <StyledInlineLink onClick={handleChromeExtensionClick}>Chrome extension</StyledInlineLink>
        </>
      );
    }

    return {
      id: step.id,
      title,
      isCompleted: !!step.completedAt,
      shouldShowHelp: !!config?.helpContent,
      tooltip: config?.tooltip,
    };
  });

  // Show onboarding tooltip based on store state
  const shouldShowOnboardingTooltip = gettingStartedStore.isOnboardingTooltipVisible;

  return (
    <StyledContainer>
      <GettingStartedCard
        steps={steps}
        isAllComplete={gettingStartedStore.isComplete}
        progress={Math.round(gettingStartedStore.progress * 100)}
        completedCount={gettingStartedStore.completedCount}
        totalCount={gettingStartedStore.totalCount}
        initiallyCollapsed={gettingStartedStore.isCollapsed}
        hasShownConfetti={gettingStartedStore.hasShownConfetti}
        onboardingTooltip={
          shouldShowOnboardingTooltip
            ? {
                title: "Start here",
                subtitle: "Learn the basics of capturing your meetings and thoughts in Mem",
                isOpen: true,
                onDismiss: gettingStartedStore.dismissOnboardingTooltip,
              }
            : undefined
        }
        onHelpClick={handleHelpClick}
        onToggleExpanded={gettingStartedStore.toggleGuide}
        onExploreMoreClick={handleExploreMoreClick}
        onDismiss={gettingStartedStore.dismissGuide}
        onConfettiShown={gettingStartedStore.markConfettiShown}
      />
      {helpModalStepId && (
        <GettingStartedHelpModal
          isOpen={true}
          stepId={helpModalStepId}
          onClose={handleCloseHelpModal}
        />
      )}
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  margin-bottom: 45px;
`;
